import React, { useEffect } from "react";
import SubmitButton from "../components/buttons/SubmitButton";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  CircularProgress,
  Grid,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import FormTextField from "../components/common/FormTextField";
import { Formik, Field } from "formik";
import * as Yup from "yup";
import { createRole, updateRole } from "../redux/actions/roleActions";
import { getPermissions } from "../redux/actions/permissionActions";
import { cleanFormData } from "../helpers/formHelpers";

// Validation Schema
const roleValidationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Role name is required")
    .matches(
      /^[A-Z_]*$/,
      "Role name must be uppercase with underscores (e.g., SUPER_ADMIN)",
    ),
  description: Yup.string().required("Description is required"),
  permissions: Yup.array().of(Yup.string()),
});

// Helper to group permissions (e.g., user:create -> User)
const groupPermissions = (permissions) => {
  return permissions.reduce((acc, permissionObj) => {
    const permission = permissionObj.name;

    const [group] = permission.split(":");

    if (!acc[group]) {
      acc[group] = [];
    }

    acc[group].push(permission);
    return acc;
  }, {});
};

const CreateOrUpdateRoleForm = ({
  role = null,
  isEditing = false,
  loading = false,
}) => {
  const dispatch = useDispatch();
  const { permissions, loadingPermissions } = useSelector(
    (state) => state.permission,
  );

  // Fetch all available permissions when the component mounts for the update form
  useEffect(() => {
    if (isEditing && permissions.length === 0) {
      dispatch(getPermissions());
    }
  }, [dispatch, isEditing, permissions.length]);

  const initialValues = {
    name: role?.name || "",
    description: role?.description || "",
    permissions: role?.permissions || [],
  };

  const grouped = groupPermissions(permissions);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={roleValidationSchema}
      onSubmit={(values) => {
        const cleanedValues = cleanFormData(values);
        if (isEditing) {
          dispatch(updateRole({ id: role.id, values: cleanedValues }));
        } else {
          dispatch(createRole(cleanedValues));
        }
      }}
      enableReinitialize={true}
    >
      {({
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        handleSubmit,
        setFieldValue,
      }) => (
        <form onSubmit={handleSubmit}>
          <FormTextField
            name="name"
            label="Role Name (e.g., SUPER_ADMIN)"
            disabled={isEditing}
          />

          <FormTextField
            name="description"
            label="Description"
            multiline
            rows={2}
          />

          {isEditing && (
            <Box mt={3}>
              <Typography variant="h6" gutterBottom>
                Permissions
              </Typography>
              {loadingPermissions ? (
                <CircularProgress />
              ) : (
                <Grid
                  container
                  spacing={2}
                  sx={{ maxHeight: "40vh", overflowY: "auto", p: 1 }}
                >
                  {Object.keys(grouped)
                    .sort()
                    .map((groupName) => (
                      <Grid item xs={12} sm={6} md={4} key={groupName}>
                        <Box
                          border={1}
                          borderColor="divider"
                          borderRadius={1}
                          p={2}
                          height="100%"
                        >
                          <Typography
                            variant="subtitle1"
                            component="div"
                            sx={{ textTransform: "capitalize" }}
                          >
                            {groupName}
                          </Typography>
                          <FormGroup>
                            {grouped[groupName].sort().map((permission) => (
                              <FormControlLabel
                                key={permission}
                                control={
                                  <Field
                                    as={Checkbox}
                                    type="checkbox"
                                    name="permissions"
                                    value={permission}
                                    checked={values.permissions.includes(
                                      permission,
                                    )}
                                    onChange={(e) => {
                                      const newPermissions = e.target.checked
                                        ? [...values.permissions, permission]
                                        : values.permissions.filter(
                                            (p) => p !== permission,
                                          );
                                      setFieldValue(
                                        "permissions",
                                        newPermissions,
                                      );
                                    }}
                                  />
                                }
                                label={permission.split(":")[1]} // Show 'create' instead of 'user:create'
                              />
                            ))}
                          </FormGroup>
                        </Box>
                      </Grid>
                    ))}
                </Grid>
              )}
            </Box>
          )}

          <Box mt={3}>
            <SubmitButton
              title={isEditing ? "Save Changes" : "Create Role"}
              loadingText={isEditing ? "Saving..." : "Creating..."}
              loading={loading}
              startIcon={
                isEditing ? <SaveOutlinedIcon /> : <AddCircleOutlineIcon />
              }
            />
          </Box>
        </form>
      )}
    </Formik>
  );
};

export default CreateOrUpdateRoleForm;
